import styled from 'styled-components'

import { COLORS } from '../../commons/Colors'
import LoadingComponent from '../../commons/components/LoadingComponent'
import MainHeaderComponent from '../../commons/components/MainHeaderComponent'
import RoundButtonComponent from '../../commons/components/RoundButtonComponent'
import SnackBarComponent from '../../commons/components/SnackBarComponent'
import { SnackbarConfigTypes } from '../../commons/utils/Types'
import { User } from '../../models/User'
import GlobalThemeProvider from '../../style/GlobalTheme'
import { formatDocument } from '../../utils/Utils'

type UserSearchViewProps = {
    isLoading: boolean
    legacyCode: string
    user: User | null
    snackbarConfig: SnackbarConfigTypes
    handleCloseSnackbar: () => void
    handleLegacyCodeChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    handleSearch: () => void
}

function UserSearchView({
    isLoading,
    legacyCode,
    user,
    snackbarConfig,
    handleCloseSnackbar,
    handleLegacyCodeChange,
    handleSearch,
}: UserSearchViewProps): JSX.Element {
    return (
        <GlobalThemeProvider>
            <PageContainer>
                <MainHeaderComponent />
                <PageTitle>Busca de Usuário - Time DSM-Firmenich</PageTitle>
                {isLoading ? (
                    <LoadingComponent />
                ) : (
                    <MainContainer>
                        <SubTitle>BUSCAR USUÁRIO POR CÓDIGO</SubTitle>
                        <SearchContainer>
                            <InputContainer>
                                <LabelField htmlFor="legacy-code-input">
                                    Código do usuário
                                </LabelField>
                                <InputField
                                    id="legacy-code-input"
                                    placeholder="Digite o código do usuário"
                                    value={legacyCode}
                                    onChange={handleLegacyCodeChange}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearch()
                                        }
                                    }}
                                />
                            </InputContainer>
                            <ButtonContainer>
                                <RoundButtonComponent
                                    text="Buscar"
                                    size="large"
                                    onClick={handleSearch}
                                />
                            </ButtonContainer>
                        </SearchContainer>
                        {user && (
                            <UserInfoContainer>
                                <UserInfoTitle>
                                    Informações do Usuário
                                </UserInfoTitle>
                                <UserInfoGrid>
                                    <UserInfoItem>
                                        <InfoLabel>Código:</InfoLabel>
                                        <InfoValue>{user.legacyCode}</InfoValue>
                                    </UserInfoItem>
                                    <UserInfoItem>
                                        <InfoLabel>Login:</InfoLabel>
                                        <InfoValue>{user.login}</InfoValue>
                                    </UserInfoItem>
                                    <UserInfoItem>
                                        <InfoLabel>Email:</InfoLabel>
                                        <InfoValue>{user.email}</InfoValue>
                                    </UserInfoItem>
                                    <UserInfoItem>
                                        <InfoLabel>Nome:</InfoLabel>
                                        <InfoValue>{user.name}</InfoValue>
                                    </UserInfoItem>
                                    <UserInfoItem>
                                        <InfoLabel>
                                            Senha de Comissão Expirada:
                                        </InfoLabel>
                                        <InfoValue>
                                            {user.expiredComissionPass
                                                ? 'Sim'
                                                : 'Não'}
                                        </InfoValue>
                                    </UserInfoItem>
                                    <UserInfoItem>
                                        <InfoLabel>Senha Expirada:</InfoLabel>
                                        <InfoValue>
                                            {user.expiredPass ? 'Sim' : 'Não'}
                                        </InfoValue>
                                    </UserInfoItem>
                                    <UserInfoItem>
                                        <InfoLabel>
                                            Número de Celular:
                                        </InfoLabel>
                                        <InfoValue>
                                            {user.mobileNumber || '-'}
                                        </InfoValue>
                                    </UserInfoItem>
                                    <UserInfoItem>
                                        <InfoLabel>2FA Habilitado:</InfoLabel>
                                        <InfoValue>
                                            {user.twoFAEnabled ? 'Sim' : 'Não'}
                                        </InfoValue>
                                    </UserInfoItem>
                                    <UserInfoItem>
                                        <InfoLabel>Função:</InfoLabel>
                                        <InfoValue>
                                            {user.role || '-'}
                                        </InfoValue>
                                    </UserInfoItem>
                                    <UserInfoItem>
                                        <InfoLabel>Documento:</InfoLabel>
                                        <InfoValue>
                                            {formatDocument(user.document)}
                                        </InfoValue>
                                    </UserInfoItem>
                                    <UserInfoItem>
                                        <InfoLabel>
                                            Última atualização:
                                        </InfoLabel>
                                        <InfoValue>
                                            {new Date(
                                                user.updatedAt
                                            ).toLocaleString('pt-BR')}
                                        </InfoValue>
                                    </UserInfoItem>
                                </UserInfoGrid>
                            </UserInfoContainer>
                        )}
                    </MainContainer>
                )}
            </PageContainer>
            <SnackBarComponent
                message={snackbarConfig.message}
                isVisible={snackbarConfig.isVisible}
                severity={snackbarConfig.status}
                handleClose={handleCloseSnackbar}
            />
        </GlobalThemeProvider>
    )
}

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background-color: ${COLORS.secondaryWhite};
`

const PageTitle = styled.h1`
    color: ${COLORS.primaryOrange};
    font-size: 18px;
    font-family: Roboto;
    margin: auto;
    font-weight: normal;
    margin-top: 25px;
`

const MainContainer = styled.div`
    background-color: ${COLORS.primaryWhite};
    border: solid 1px ${COLORS.borderGray};
    border-radius: 3px;
    display: flex;
    flex-direction: column;
    margin: 30px;
    padding: 25px 25px 50px 25px;
`

const SubTitle = styled.h1`
    align: start;
    color: ${COLORS.primaryGray};
    font-size: 12px;
    font-family: Roboto;
    font-weight: bold;
    text-align: start;
    margin-bottom: 40px;
`

const SearchContainer = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 20px;
`

const InputContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 30%;
`

const LabelField = styled.label`
    color: ${COLORS.primaryGray};
    font-family: Roboto;
    font-size: 12px;
    font-weight: bold;
    line-height: 2;
    margin-bottom: 5px;
    text-transform: uppercase;
`

const InputField = styled.input`
    border: 1px solid ${COLORS.disabledButton};
    border-radius: 4px;
    font-family: Roboto;
    font-size: 14px;
    height: 30px;
    line-height: 1.43;
    padding: 8px;

    :focus {
        outline-color: ${COLORS.primaryOrange};
    }
`

const ButtonContainer = styled.div`
    max-height: 40px;
`

const UserInfoContainer = styled.div`
    margin-top: 40px;
    padding-top: 40px;
    border-top: 1px solid ${COLORS.borderGray};
`

const UserInfoTitle = styled.h2`
    color: ${COLORS.primaryGray};
    font-size: 16px;
    font-family: Roboto;
    font-weight: bold;
    margin-bottom: 20px;
`

const UserInfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 15px 30px;
`

const UserInfoItem = styled.div`
    display: flex;
    flex-direction: column;
`

const InfoLabel = styled.span`
    color: ${COLORS.primaryGray};
    font-family: Roboto;
    font-size: 12px;
    font-weight: bold;
    margin-bottom: 5px;
`

const InfoValue = styled.span`
    color: ${COLORS.primaryGray};
    font-family: Roboto;
    font-size: 14px;
    line-height: 1.43;
`

export default UserSearchView
